import {
  instantDateKeyOf,
  instantTimeOf,
  occurrenceDateRange,
  pinOccurrenceToSingleEvent,
  type EventException,
  type RecurrenceFreq,
  type ScheduleEvent,
} from "@couples/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";

import { errorMessageOf } from "../lib/auth";
import { mixHexColors, withAlpha } from "../lib/color";
import { queryKeys } from "../lib/queryKeys";
import {
  createEventCategory,
  fetchEventCategories,
  normalizeCategoryName,
} from "../lib/schedule/categories";
import type { EventInput } from "../lib/schedule/events";
import { downloadEventIcs } from "../lib/schedule/ics";
import { ACCENT_CHOICES, DEFAULT_ACCENT } from "../lib/theme";
import { useToast } from "../lib/toast";
import TimeSlider from "./TimeSlider";
import SheetShell from "./SheetShell";

/** JSTは夏時間が無いため固定の +09:00 で組み立てられる(→ packages/shared/src/timeline.ts と同じ考え方) */
function toInstant(dateKey: string, timeKey: string): string {
  return new Date(`${dateKey}T${timeKey}:00+09:00`).toISOString();
}

const RECURRENCE_OPTIONS: {
  value: RecurrenceFreq | "none";
  label: string;
  intervalUnit: string;
}[] = [
  { value: "none", label: "繰り返さない", intervalUnit: "" },
  { value: "daily", label: "毎日", intervalUnit: "日ごと" },
  { value: "weekly", label: "毎週", intervalUnit: "週ごと" },
  { value: "monthly", label: "毎月", intervalUnit: "ヶ月ごと" },
  { value: "yearly", label: "毎年", intervalUnit: "年ごと" },
];

interface FormState {
  title: string;
  memo: string;
  allDay: boolean;
  date: string;
  endDate: string;
  startTime: string;
  endTime: string;
  assignedTo: string | null;
  categoryId: string | null;
  recurrenceFreq: RecurrenceFreq | "none";
  recurrenceInterval: string;
  recurrenceUntil: string;
}

function initialStateOf(
  editing: ScheduleEvent | undefined,
  initialDate: string,
  occurrenceOnly: boolean,
): FormState {
  if (editing === undefined) {
    return {
      title: "",
      memo: "",
      allDay: false,
      date: initialDate,
      endDate: initialDate,
      startTime: "10:00",
      endTime: "11:00",
      assignedTo: null,
      categoryId: null,
      recurrenceFreq: "none",
      recurrenceInterval: "1",
      recurrenceUntil: "",
    };
  }
  // 「この回だけ」編集では、タップした回の実際の日付(initialDate)を起点にする。
  // それ以外(シリーズ全体の編集)は従来どおりシリーズの起点日付を使う
  // (initialDate はどの回をタップしたかに関わらず無視する)。
  const { date, endDate } = occurrenceOnly
    ? occurrenceDateRange(editing, initialDate)
    : {
        date: instantDateKeyOf(editing.startsAt),
        endDate:
          editing.endsAt !== null
            ? instantDateKeyOf(editing.endsAt)
            : instantDateKeyOf(editing.startsAt),
      };
  return {
    title: editing.title,
    memo: editing.memo ?? "",
    allDay: editing.allDay,
    date,
    endDate,
    startTime: instantTimeOf(editing.startsAt),
    endTime: editing.endsAt !== null ? instantTimeOf(editing.endsAt) : "11:00",
    assignedTo: editing.assignedTo,
    categoryId: editing.categoryId,
    recurrenceFreq: occurrenceOnly ? "none" : (editing.recurrenceFreq ?? "none"),
    recurrenceInterval: String(editing.recurrenceInterval),
    recurrenceUntil: editing.recurrenceUntil ?? "",
  };
}

interface Props {
  editing?: ScheduleEvent;
  initialDate: string;
  /** 繰り返し予定のうち、この回だけを編集・差し替える(→ Schedule.tsx の scope 選択) */
  occurrenceOnly?: boolean;
  /** ICS書き出し(シリーズ全体を書き出すときの EXDATE 計算用)。→ handleExportIcs */
  exceptions: EventException[];
  selfId: string;
  selfName: string;
  partnerId: string | null;
  partnerName: string;
  selfColor: string;
  partnerColor: string;
  busy: boolean;
  onSave: (input: EventInput) => void;
  onDelete?: () => void;
  onClose: () => void;
}

export default function EventSheet({
  editing,
  initialDate,
  occurrenceOnly = false,
  exceptions,
  selfId,
  selfName,
  partnerId,
  partnerName,
  selfColor,
  partnerColor,
  busy,
  onSave,
  onDelete,
  onClose,
}: Props) {
  const [form, setForm] = useState<FormState>(() =>
    initialStateOf(editing, initialDate, occurrenceOnly),
  );
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [newCategoryColor, setNewCategoryColor] = useState(DEFAULT_ACCENT);

  const toast = useToast();
  const queryClient = useQueryClient();
  const categories = useQuery({
    queryKey: queryKeys.eventCategories,
    queryFn: fetchEventCategories,
  });

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const category of categories.data ?? []) map.set(category.id, category.name);
    return map;
  }, [categories.data]);

  const addCategory = useMutation({
    mutationFn: async (raw: string) => {
      const name = normalizeCategoryName(raw);
      if (name === null) return null;
      return createEventCategory(name, newCategoryColor);
    },
    onSuccess: async (category) => {
      setNewCategory("");
      setNewCategoryColor(DEFAULT_ACCENT);
      setAddingCategory(false);
      await queryClient.invalidateQueries({ queryKey: queryKeys.eventCategories });
      if (category !== null) set({ categoryId: category.id });
    },
    onError: (error) => {
      toast(errorMessageOf(error), "error");
    },
  });

  function set(patch: Partial<FormState>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    const title = form.title.trim();
    if (title === "") return;

    const freq = form.recurrenceFreq === "none" ? null : form.recurrenceFreq;
    const interval = freq === null ? 1 : Math.max(1, Number(form.recurrenceInterval) || 1);

    const input: EventInput = {
      title,
      memo: form.memo.trim() === "" ? null : form.memo.trim(),
      allDay: form.allDay,
      assignedTo: form.assignedTo,
      categoryId: form.categoryId,
      startsAt: form.allDay ? toInstant(form.date, "00:00") : toInstant(form.date, form.startTime),
      // 終日で単日なら endsAt は null のまま(→ lib/schedule/events.ts の既存の単日表現を崩さない)
      endsAt: form.allDay
        ? form.endDate !== form.date
          ? toInstant(form.endDate, "00:00")
          : null
        : toInstant(form.endDate, form.endTime),
      recurrenceFreq: freq,
      recurrenceInterval: interval,
      recurrenceUntil:
        freq === null ? null : form.recurrenceUntil === "" ? null : form.recurrenceUntil,
    };
    onSave(input);
  }

  /**
   * 予定の詳細(この編集シート)から ICS 書き出し。
   * 「この回だけ」編集中は、その回だけを単発の予定として切り出して書き出す
   * (→ pinOccurrenceToSingleEvent)。それ以外はシリーズそのもの(繰り返し込み)を書き出す。
   */
  function handleExportIcs() {
    if (editing === undefined) return;
    try {
      const exported = occurrenceOnly
        ? pinOccurrenceToSingleEvent({ event: editing, date: initialDate, startDate: initialDate })
        : editing;
      downloadEventIcs(exported, occurrenceOnly ? [] : exceptions, categoryNameById);
      toast("書き出しました。");
    } catch (error) {
      toast(errorMessageOf(error), "error");
    }
  }

  const bothColor = mixHexColors(selfColor, partnerColor);
  const whoOptions: { value: string | null; label: string; color: string }[] = [
    { value: selfId, label: selfName, color: selfColor },
    ...(partnerId !== null ? [{ value: partnerId, label: partnerName, color: partnerColor }] : []),
    { value: null, label: "2人", color: bothColor },
  ];

  const recurrenceOption = RECURRENCE_OPTIONS.find(
    (option) => option.value === form.recurrenceFreq,
  );

  return (
    <SheetShell
      title={
        <>
          {editing !== undefined ? "予定を編集" : "予定を追加"}
          {occurrenceOnly && (
            <span className="mt-0.5 block text-[10.5px] font-normal text-[var(--color-sub)]">
              この回だけに反映されます
            </span>
          )}
        </>
      }
      actions={
        editing !== undefined && (
          <button type="button" onClick={handleExportIcs} aria-label="この予定を書き出し(.ics)">
            <Download className="size-5 text-[var(--color-sub)]" aria-hidden />
          </button>
        )
      }
      onClose={onClose}
      elevated
      tone="bg"
    >
      <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto p-4">
          <label className="block">
            <span className="mb-1 block text-[11px] text-[var(--color-sub)]">タイトル</span>
            <input
              autoFocus
              value={form.title}
              onChange={(event) => {
                set({ title: event.target.value });
              }}
              placeholder="例: 花火大会"
              className="w-full rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none"
            />
          </label>

          <div className="flex gap-2.5">
            <label className="flex-1">
              <span className="mb-1 block text-[11px] text-[var(--color-sub)]">開始日</span>
              <input
                type="date"
                value={form.date}
                onChange={(event) => {
                  const date = event.target.value;
                  // 開始日を終了日より後にしたら、終了日も一緒に繰り上げる
                  set({ date, endDate: form.endDate < date ? date : form.endDate });
                }}
                className="w-full rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none"
              />
            </label>
            <label className="flex-1">
              <span className="mb-1 block text-[11px] text-[var(--color-sub)]">終了日</span>
              <input
                type="date"
                min={form.date}
                value={form.endDate}
                onChange={(event) => {
                  set({ endDate: event.target.value });
                }}
                className="w-full rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none"
              />
            </label>
          </div>

          <label className="flex items-center gap-1.5 text-[12.5px]">
            <input
              type="checkbox"
              checked={form.allDay}
              onChange={(event) => {
                set({ allDay: event.target.checked });
              }}
            />
            終日
          </label>

          {!form.allDay && (
            <div className="flex gap-2.5">
              <label className="flex-1">
                <span className="mb-1 block text-[11px] text-[var(--color-sub)]">開始</span>
                <input
                  type="time"
                  value={form.startTime}
                  onChange={(event) => {
                    set({ startTime: event.target.value });
                  }}
                  className="w-full rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none"
                />
                <div className="mt-2 px-0.5">
                  <TimeSlider
                    value={form.startTime}
                    onChange={(startTime) => {
                      set({ startTime });
                    }}
                    accentClassName="accent-[var(--color-teal)]"
                  />
                </div>
              </label>
              <label className="flex-1">
                <span className="mb-1 block text-[11px] text-[var(--color-sub)]">終了</span>
                <input
                  type="time"
                  value={form.endTime}
                  onChange={(event) => {
                    set({ endTime: event.target.value });
                  }}
                  className="w-full rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none"
                />
                <div className="mt-2 px-0.5">
                  <TimeSlider
                    value={form.endTime}
                    onChange={(endTime) => {
                      set({ endTime });
                    }}
                    accentClassName="accent-[var(--color-plum)]"
                  />
                </div>
              </label>
            </div>
          )}

          <div>
            <span className="mb-1 block text-[11px] text-[var(--color-sub)]">誰の予定か</span>
            <div className="flex gap-2">
              {whoOptions.map((option) => {
                const selected = form.assignedTo === option.value;
                return (
                  <button
                    key={option.label}
                    type="button"
                    onClick={() => {
                      set({ assignedTo: option.value });
                    }}
                    className={`flex-1 rounded-lg border-[1.5px] py-2 text-[12px] font-semibold ${
                      selected ? "" : "border-[var(--color-line)] bg-white text-[var(--color-sub)]"
                    }`}
                    style={
                      selected
                        ? {
                            borderColor: option.color,
                            backgroundColor: withAlpha(option.color, "1a"),
                            color: option.color,
                          }
                        : undefined
                    }
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <span className="mb-1 block text-[11px] text-[var(--color-sub)]">カテゴリ(任意)</span>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => {
                  set({ categoryId: null });
                }}
                className={`rounded-full border-[1.5px] px-2.5 py-1 text-[12px] ${
                  form.categoryId === null
                    ? "border-[var(--color-teal)] bg-[var(--color-teal)]/10 text-[var(--color-teal)]"
                    : "border-[var(--color-line)] bg-white text-[var(--color-sub)]"
                }`}
              >
                なし
              </button>
              {categories.data?.map((category) => {
                const selected = form.categoryId === category.id;
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => {
                      set({ categoryId: category.id });
                    }}
                    className={`flex items-center gap-1 rounded-full border-[1.5px] px-2.5 py-1 text-[12px] ${
                      selected ? "" : "border-[var(--color-line)] bg-white text-[var(--color-sub)]"
                    }`}
                    style={
                      selected
                        ? {
                            borderColor: category.color,
                            backgroundColor: withAlpha(category.color, "1a"),
                            color: category.color,
                          }
                        : undefined
                    }
                  >
                    <span
                      className="size-1.5 shrink-0 rounded-full"
                      style={{ backgroundColor: category.color }}
                      aria-hidden
                    />
                    {category.name}
                  </button>
                );
              })}
              {addingCategory ? (
                <div className="flex flex-col gap-1.5">
                  <div className="flex gap-1">
                    <input
                      autoFocus
                      value={newCategory}
                      onChange={(event) => {
                        setNewCategory(event.target.value);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          event.stopPropagation();
                          addCategory.mutate(newCategory);
                        }
                      }}
                      placeholder="カテゴリ名"
                      className="w-24 rounded-full border border-[var(--color-line)] px-2.5 py-1 text-base outline-none"
                    />
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        addCategory.mutate(newCategory);
                      }}
                      className="rounded-full px-2.5 py-1 text-[12px] text-white"
                      style={{ backgroundColor: newCategoryColor }}
                    >
                      追加
                    </button>
                  </div>
                  <div className="flex gap-1.5 px-0.5">
                    {ACCENT_CHOICES.map((choice) => (
                      <button
                        key={choice.value}
                        type="button"
                        aria-label={choice.name}
                        onClick={() => {
                          setNewCategoryColor(choice.value);
                        }}
                        className={`size-5 shrink-0 rounded-full ${
                          newCategoryColor === choice.value
                            ? "ring-2 ring-offset-1 ring-[var(--color-ink)]/40"
                            : ""
                        }`}
                        style={{ backgroundColor: choice.value }}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setAddingCategory(true);
                  }}
                  className="flex items-center gap-0.5 rounded-full border-[1.5px] border-dashed border-[var(--color-line)] px-2.5 py-1 text-[12px] text-[var(--color-sub)]"
                >
                  <Plus className="size-3" aria-hidden />
                  追加
                </button>
              )}
            </div>
          </div>

          {/* 「この回だけ」の予定は単発の差し替えとして保存するため、ここでは繰り返しを設定できない */}
          {!occurrenceOnly && (
            <div>
              <span className="mb-1 block text-[11px] text-[var(--color-sub)]">繰り返し</span>
              <select
                value={form.recurrenceFreq}
                onChange={(event) => {
                  set({ recurrenceFreq: event.target.value as RecurrenceFreq | "none" });
                }}
                className="w-full rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-sm outline-none"
              >
                {RECURRENCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!occurrenceOnly && form.recurrenceFreq !== "none" && recurrenceOption !== undefined && (
            <div className="flex gap-2.5">
              <label className="flex-1">
                <span className="mb-1 block text-[11px] text-[var(--color-sub)]">間隔</span>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min={1}
                    value={form.recurrenceInterval}
                    onChange={(event) => {
                      set({ recurrenceInterval: event.target.value });
                    }}
                    className="w-16 rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none"
                  />
                  <span className="text-[12px] text-[var(--color-sub)]">
                    {recurrenceOption.intervalUnit}
                  </span>
                </div>
              </label>
              <label className="flex-1">
                <span className="mb-1 block text-[11px] text-[var(--color-sub)]">終了日(任意)</span>
                <input
                  type="date"
                  min={form.date}
                  value={form.recurrenceUntil}
                  onChange={(event) => {
                    set({ recurrenceUntil: event.target.value });
                  }}
                  className="w-full rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none"
                />
              </label>
            </div>
          )}

          <label className="block">
            <span className="mb-1 block text-[11px] text-[var(--color-sub)]">メモ(任意)</span>
            <textarea
              value={form.memo}
              onChange={(event) => {
                set({ memo: event.target.value });
              }}
              placeholder="持ち物や場所など"
              rows={2}
              className="w-full resize-none rounded-lg border border-[var(--color-line)] bg-white px-2.5 py-2 text-base outline-none"
            />
          </label>
        </div>

        <div className="flex gap-2.5 border-t border-[var(--color-line)] p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          {onDelete !== undefined && (
            <button
              type="button"
              onClick={onDelete}
              disabled={busy}
              className="flex items-center gap-1.5 rounded-lg border border-[var(--color-line)] px-3.5 text-[12.5px] text-[var(--color-plum)] disabled:opacity-50"
            >
              <Trash2 className="size-3.5" aria-hidden />
              削除
            </button>
          )}
          <button
            type="submit"
            disabled={busy || form.title.trim() === ""}
            className="flex-1 rounded-xl bg-[var(--color-teal)] py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            保存
          </button>
        </div>
      </form>
    </SheetShell>
  );
}
